import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/pro-solid-svg-icons/faTrash';
import React, { useState, useCallback, useRef } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { BehaviorSubject, of } from 'rxjs';
import { filter, debounceTime, switchMap, catchError, map } from 'rxjs/operators';

import { getProductSearchResults } from '../../../store/products/products.api';
import { noop } from 'ui-components/default-props/noop';
import { OrderItemPropType } from '../../../prop-types/order-item';
import { Input } from 'ui-components/components/input';
import { Button } from 'ui-components/components/button';
import { CurrencyInput } from 'ui-components/components/currency-input';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { testId } from 'ui-components/services/testid-service';

import messages from './messages';
import ProductAutoComplete from './product-auto-complete';
import style from './style.module.less';
import { USD } from '../../../constants/currency-codes';
import { emptyArray } from 'ui-components/default-props/empty';
import { useOnComponentMount } from '../../../hooks/use-on-component-mount';

const EditableOrderItems = ({
  onAddItem,
  orderItems = emptyArray,
  onDelete,
  onChange,
  hasError = false,
  shouldRenderSaveAndCancel = false,
  focusedIndex = -1,
  focusedField = '',
  onSave = noop,
  onClickOffSearchResults,
  onCancel = noop,
  onSearchResultSelected,
  setIsSavingItemEdits = noop,
  isSavingItemEdits = false,
}) => {
  const node = useRef();
  const [searchResults, setSearchResults] = useState([]);

  const behaviorSubject = useRef(new BehaviorSubject(''));
  const searchResult$ = behaviorSubject.current.pipe(
    filter((value) => value.length >= 3),
    debounceTime(500),
    switchMap((value) => {
      return getProductSearchResults(value).pipe(
        map((results) => results),
        catchError(() => of([])),
      );
    }),
  );

  const handleClick = useCallback(
    (e) => {
      // this is here to close search results if you click off
      if (node.current && node.current.contains(e.target)) {
        return;
      }
      onClickOffSearchResults();
    },
    [onClickOffSearchResults],
  );

  useOnComponentMount(() => {
    document.addEventListener('mousedown', handleClick, false);
    const subscription = searchResult$.subscribe((newResults) => setSearchResults(newResults));

    return () => {
      document.removeEventListener('mousedown', handleClick, false);
      subscription.unsubscribe();
    };
  });

  const onAutoCompleteChange = useCallback(
    (value, field, index) => {
      onChange(value, field, index);
      behaviorSubject.current.next(value);
      if (value.length < 3) {
        setSearchResults([]);
      }
    },
    [onChange],
  );

  const resultSelected = (item, index) => {
    onSearchResultSelected(item, index);
    setSearchResults([]);
  };

  const setQuantity = useCallback(
    (value, index) => {
      const parsed = value === '' ? value : Number.parseInt(value, 10);
      onChange(parsed, 'quantity', index);
    },
    [onChange],
  );

  const cancelEdit = useCallback(() => {
    onCancel();
  }, [onCancel]);

  const saveEdit = useCallback(() => {
    setIsSavingItemEdits(true);
    onSave();
  }, [onSave, setIsSavingItemEdits]);

  const showSearchResults = useCallback(
    (field, index) => {
      if (focusedIndex !== index || focusedField !== field) {
        return null;
      }
      return (
        <div className={style['result-dropdown-menu']}>
          {searchResults.map((item, idx) => {
            return (
              <div key={idx} className={style['result-item']}>
                <DropdownMenuItem className={style.row} onClick={() => resultSelected(item, index)}>
                  <div className={style['item-name']}>{item.name}</div>
                  <div className={style['item-sku']}>{item.sku}</div>
                </DropdownMenuItem>
              </div>
            );
          })}
        </div>
      );
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- resultSelected changes every render
    [focusedField, focusedIndex, resultSelected, searchResults],
  );

  return (
    <>
      <div ref={node} className={style['order-item-container']}>
        <>
          <div className={style['header-cell']}>
            <FormattedMessage {...messages.itemHeaderSku} />
          </div>
          <div className={style['header-cell']}>
            <FormattedMessage {...messages.itemHeaderName} />
          </div>
          <div className={style['header-cell']}>
            <FormattedMessage {...messages.itemHeaderQuantity} />
          </div>
          <div className={style['last-header-cell']}>
            <FormattedMessage {...messages.itemHeaderPrice} />
          </div>
          <div className={style['last-header-cell']} />
        </>
        {orderItems.map((item, index) => (
          <React.Fragment key={index}>
            <ProductAutoComplete
              field='sku'
              hasError={hasError}
              index={index}
              onAutoCompleteChange={onAutoCompleteChange}
              searchResults={searchResults}
              showSearchResults={showSearchResults}
              value={item.sku}
            />
            <ProductAutoComplete
              field='name'
              hasError={!item.name && hasError}
              index={index}
              onAutoCompleteChange={onAutoCompleteChange}
              searchResults={searchResults}
              showSearchResults={showSearchResults}
              value={item.name}
            />
            <Input
              className={style['quantity-input']}
              {...testId(`${item.sku}-quantity-input`)}
              hasError={!item.quantity && hasError}
              onChange={(value) => setQuantity(value, index)}
              value={item.quantity.toString()}
            />
            <CurrencyInput
              className={classnames(style['currency-input'], {
                [style.error]: (!item.unitPrice?.value || item.unitPrice.value <= 0) && hasError,
              })}
              currencyCode={item.unitPrice?.code || USD}
              disallowNegative
              onChange={(value) => onChange({ ...item.unitPrice, value: parseFloat(value) }, 'unitPrice', index)}
              step='0.01'
              value={item.unitPrice.value}
            />
            <Button className={style['delete-item']} onClick={() => onDelete(index)} theme='unstyled'>
              <FontAwesomeIcon icon={faTrash} />
            </Button>
          </React.Fragment>
        ))}
      </div>
      <div className={style['buttons-row']}>
        <div className={style['add-item-button-container']}>
          <Button className={style['add-item-button']} onClick={onAddItem} theme='white'>
            <FormattedMessage {...messages.addLineItem} />
          </Button>
        </div>
        {shouldRenderSaveAndCancel ? (
          <div className={style['save-and-cancel']}>
            <Button className={style['cancel-edit-items']} onClick={cancelEdit} theme='tertiary'>
              <FormattedMessage defaultMessage='Cancel' description='orderItemsWrapper.cancel' id='3EoCZG' />
            </Button>
            <Button className={style['save-edit-items']} disabled={isSavingItemEdits} onClick={saveEdit} theme='primary'>
              <FormattedMessage defaultMessage='Save' description='orderItemsWrapper.save' id='ZM4aOi' />
            </Button>
          </div>
        ) : null}
      </div>
    </>
  );
};

EditableOrderItems.propTypes = {
  orderItems: PropTypes.arrayOf(OrderItemPropType),
  onChange: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  onSave: PropTypes.func,
  onAddItem: PropTypes.func.isRequired,
  onSearchResultSelected: PropTypes.func.isRequired,
  onClickOffSearchResults: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  shouldRenderSaveAndCancel: PropTypes.bool,
  focusedIndex: PropTypes.number,
  focusedField: PropTypes.string,
  hasError: PropTypes.bool,
  setIsSavingItemEdits: PropTypes.func,
  isSavingItemEdits: PropTypes.bool,
};

export default EditableOrderItems;
